class JptMemberGate extends HTMLElement {
  connectedCallback() {
    const state = document.body.dataset.jptMemberState;
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const exempt = path === '/pages/authenticate' || path.startsWith('/account');

    if (state === 'registration-required' && !exempt) {
      window.location.replace('/pages/authenticate');
    }

    if (path === '/pages/authenticate' && state === 'approved') {
      window.location.replace('/');
    }

    if (path === '/cart' && ['unauthenticated', 'pending'].includes(state)) {
      window.location.replace('/');
    }
  }
}

if (!customElements.get('jpt-member-gate')) {
  customElements.define('jpt-member-gate', JptMemberGate);
}

class JptAuthenticateForm extends HTMLElement {
  connectedCallback() {
    this.prepare = this.prepare.bind(this);
    this.observedShadowRoots = new WeakSet();
    this.prepare();
    this.observer = new MutationObserver(this.prepare);
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  prepare() {
    const email = document.body.dataset.jptCustomerEmail;
    const outerRoot = document.querySelector('[data-form-root="true"]');

    if (!outerRoot) return;

    const searchRoots = [outerRoot];
    for (let index = 0; index < searchRoots.length; index += 1) {
      searchRoots[index].querySelectorAll?.('*').forEach((element) => {
        if (element.shadowRoot && !searchRoots.includes(element.shadowRoot)) {
          searchRoots.push(element.shadowRoot);
        }
      });
    }

    searchRoots.forEach((root) => {
      if (!(root instanceof ShadowRoot) || this.observedShadowRoots.has(root)) return;
      this.observer.observe(root, { childList: true, subtree: true });
      this.observedShadowRoots.add(root);
    });

    const form = searchRoots.map((root) => root.querySelector?.('[data-testid="form"]')).find(Boolean);

    if (!form) return;

    const formRoot = form.closest('[data-form-root="true"]') || form.getRootNode();
    const shadowRoot = form.getRootNode() instanceof ShadowRoot ? form.getRootNode() : null;
    const shadowStyles = document.querySelector('[data-jpt-authenticate-shadow-styles]');
    if (shadowRoot && shadowStyles && !shadowRoot.querySelector('[data-jpt-authenticate-shadow-style]')) {
      const styles = shadowStyles.content.cloneNode(true);
      shadowRoot.append(styles);
    }

    let formShell = form.parentElement;
    while (formShell && formShell !== formRoot) {
      formShell.classList.add('jpt-authenticate__form-shell');
      formShell = formShell.parentElement;
    }

    const normalizedText = (value) => value?.replace(/\s+/g, ' ').trim();
    const duplicateTexts = [
      normalizedText(document.querySelector('.jpt-authenticate__header h1')?.textContent),
      normalizedText(document.querySelector('.jpt-authenticate__description')?.textContent),
      normalizedText(document.querySelector('.jpt-authenticate__confirmation p')?.textContent),
    ].filter(Boolean);

    formRoot.querySelectorAll('h1, h2, h3, h4, p').forEach((element) => {
      if (duplicateTexts.includes(normalizedText(element.textContent))) {
        element.dataset.jptFormDuplicate = 'true';
      }
    });

    const getFieldContainer = (input) => {
      const marker = input.closest('[data-testid^="field-"]') || input;
      let field = marker;

      while (field.parentElement && field.parentElement !== form) {
        const parent = field.parentElement;
        const fieldCount = parent.querySelectorAll('[data-testid^="field-"]').length;
        if (fieldCount > 1) break;
        field = parent;
      }

      return field;
    };

    const fields = [];

    formRoot?.querySelectorAll('input, select, textarea').forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') return;

      const field = getFieldContainer(input);
      if (!field) return;

      const identity = `${input.name} ${input.id} ${input.autocomplete}`.toLowerCase();
      let labelText = '';
      let placeholder = '';
      let required = false;
      if (/(family|last.?name|surname)/.test(identity)) {
        field.classList.add('jpt-authenticate__field');
        field.classList.add('jpt-authenticate__field--family');
        labelText = '姓';
        placeholder = '例）山田';
        required = true;
      } else if (/(given|first.?name)/.test(identity)) {
        field.classList.add('jpt-authenticate__field');
        field.classList.add('jpt-authenticate__field--given');
        labelText = '名';
        placeholder = '例）優子';
        required = true;
      } else if (/email/.test(identity)) {
        field.classList.add('jpt-authenticate__field', 'jpt-authenticate__field--email');
        labelText = 'メールアドレス';
        placeholder = '例）yuko.yamada@example.com';
        required = true;
      } else if (/phone|tel/.test(identity)) {
        field.classList.add('jpt-authenticate__field', 'jpt-authenticate__field--phone');
        labelText = '電話番号';
        placeholder = '例）090-1234-5678';
      }

      if (field.classList.contains('jpt-authenticate__field')) {
        if (!field.querySelector(':scope > .jpt-authenticate__generated-label')) {
          const label = document.createElement('span');
          label.className = 'jpt-authenticate__generated-label';
          label.setAttribute('aria-hidden', 'true');
          label.append(document.createTextNode(labelText));
          if (required) {
            const requiredMark = document.createElement('span');
            requiredMark.textContent = '*';
            label.append(document.createTextNode(' '), requiredMark);
          }
          field.prepend(label);
        }
        input.placeholder = placeholder;
        fields.push(field);
      }
    });

    const uniqueFields = [...new Set(fields)];
    const phoneField = formRoot.querySelector('.jpt-authenticate__field--phone');
    const consentInput = formRoot.querySelector('input[type="checkbox"]');
    const consentField = consentInput ? getFieldContainer(consentInput) : null;
    if (consentField) consentField.classList.add('jpt-authenticate__consent');

    let fieldLayout = form.querySelector(':scope > [data-jpt-authenticate-fields]');
    if (!fieldLayout) {
      fieldLayout = document.createElement('div');
      fieldLayout.className = 'jpt-authenticate__fields';
      fieldLayout.dataset.jptAuthenticateFields = 'true';
      form.prepend(fieldLayout);
    }

    const orderedFields = [
      formRoot.querySelector('.jpt-authenticate__field--family'),
      formRoot.querySelector('.jpt-authenticate__field--given'),
      formRoot.querySelector('.jpt-authenticate__field--email'),
      phoneField,
      consentField,
    ].filter(Boolean);

    orderedFields.forEach((field, index) => {
      if (fieldLayout.children[index] !== field) fieldLayout.append(field);
    });

    form.querySelectorAll('.jpt-authenticate__form-shell').forEach((shell) => {
      const hasControls = shell.querySelector('input, select, textarea, button, [data-jpt-authenticate-fields]');
      shell.toggleAttribute('data-jpt-empty-form-shell', !hasControls);
    });
    form.querySelectorAll('div, fieldset, section').forEach((container) => {
      if (container === fieldLayout || container.contains(fieldLayout)) return;
      const hasContent = container.textContent.trim() || container.querySelector('input, select, textarea, button');
      container.toggleAttribute('data-jpt-empty-form-shell', !hasContent);
    });

    const phoneInput = phoneField?.querySelector('input[type="tel"], input[data-testid^="field-"]');
    if (phoneInput && !phoneInput.dataset.jptPhonePrepared && /^\+\d{1,3}\s*$/.test(phoneInput.value)) {
      phoneInput.dataset.jptPhonePrepared = 'true';
      const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      valueSetter?.call(phoneInput, '');
      phoneInput.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
      phoneInput.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }

    const submit = form?.querySelector('[data-testid="btn-form-submit"]');
    if (form && submit && !form.querySelector('.jpt-authenticate__divider')) {
      const divider = document.createElement('hr');
      divider.className = 'jpt-authenticate__divider';
      submit.before(divider);
    }

    if (!email) return;

    formRoot?.querySelectorAll('input[type="email"]').forEach((input) => {
      if (input.closest('footer, .footer, predictive-search')) return;
      input.value = email;
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
      input.dataset.jptReadonlyEmail = 'true';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}

if (!customElements.get('jpt-authenticate-form')) {
  customElements.define('jpt-authenticate-form', JptAuthenticateForm);
}
